import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type React from 'react'

import B2becSpinner from '@/components/B2becSpinner'
import CustomButton from '@/components/CustomButton'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import {
  selectIsFetchingSAPLog,
  selectSAPLogData,
  selectShouldShowSAPLogView,
} from '@/store/selectors/sapLogSelectors'
import { showSAPLogView } from '@/store/slices/SAPLogSlice'
import usePermission from '@/hooks/usePermission'
import { MODULE_DEBUGGING_RIGHTS } from '@/libs/constants/modulerights'

import styles from './SAPLog.module.scss'

const SAP_LOG_TYPE = {
  REQUEST: 'Request',
  RESPONSE: 'Response',
} as const

type SapLogType = (typeof SAP_LOG_TYPE)[keyof typeof SAP_LOG_TYPE]

interface SapLogEntry {
  type: SapLogType
  execTime: string
  [key: string]: unknown
}

interface SapLogListProps {
  logEntries: SapLogEntry[]
  title: string
}

/**
 * Displays a list of SAP log entries
 *
 * @param props - Component props
 * @param props.logEntries - Array of SAP log entries to display
 * @param props.title - Title for this group of log entries
 */
const SapLogList: React.FC<React.PropsWithChildren<SapLogListProps>> = ({
  logEntries,
  title,
}) => {
  if (logEntries.length === 0) {
    return null
  }

  return (
    <div className={styles['sap-log__list-container']}>
      <h3 className={styles['sap-log__list-title']}>{title}</h3>
      <div className={styles['sap-log__list']}>
        {logEntries.map((entry) => <SapLogEntry key={entry.execTime} entry={entry} />)}
      </div>
    </div>
  )
}

interface SapLogEntryProps {
  entry: SapLogEntry
}

/**
 * Displays a single SAP log entry
 *
 * @param props - Component props
 * @param props.entry - The SAP log entry to display
 */
const SapLogEntry: React.FC<React.PropsWithChildren<SapLogEntryProps>> = ({
  entry,
}) => <div className={styles['sap-log__list--item']}>{JSON.stringify(entry)}</div>

/**
 * SapLogViewer component displays SAP request and response logs
 * Requires debugging permission to view
 */
const SapLogViewer: React.FC<React.PropsWithChildren<unknown>> = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const sapLogData = useAppSelector(selectSAPLogData) as SapLogEntry[]
  const isFetchingSapLog = useAppSelector(selectIsFetchingSAPLog)
  const shouldShowSapLogView = useAppSelector(selectShouldShowSAPLogView)

  const { hasPermission: canReadSapLog } = usePermission(
    MODULE_DEBUGGING_RIGHTS.READ_DEBUG,
  )

  const handleCloseViewLog = useCallback(() => {
    dispatch(showSAPLogView(false))
  }, [dispatch])

  const requestLogs = useMemo(() => {
    return (sapLogData ?? []).filter(
      (entry) => entry?.type === SAP_LOG_TYPE.REQUEST,
    )
  }, [sapLogData])

  const responseLogs = useMemo(() => {
    return (sapLogData ?? []).filter(
      (item) => item?.type === SAP_LOG_TYPE.RESPONSE,
    )
  }, [sapLogData])

  if (!canReadSapLog || !shouldShowSapLogView) {
    return null
  }

  return (
    <div className={styles['sap-log__wrapper']}>
      {isFetchingSapLog && <B2becSpinner isComponentLoading isLoading={isFetchingSapLog} />}

      <div className={styles['sap-log__header']}>
        <CustomButton
          buttonSize="small"
          className="inverted"
          type="ghost"
          onClick={handleCloseViewLog}
        >
          {t('common.button.hideMessages')}
        </CustomButton>
      </div>

      <SapLogList
        logEntries={requestLogs}
        title={t('debugging.sapLog.requestTitle', 'SAP Requests')}
      />

      <SapLogList
        logEntries={responseLogs}
        title={t('debugging.sapLog.responseTitle', 'SAP Responses')}
      />
    </div>
  )
}

export default SapLogViewer
